import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Spin } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { countryCodes } from './countryCodes';
import businessUnitService from '../services/businessUnitService';

const { Option } = Select;

const PhoneNumberInput = ({
  form,
  name = 'phoneNumber',
  label = 'Phone Number',
  required = true,
  placeholder = '123456789',
  size = 'large',
  currentPhoneNumber = null, // To skip validation for current user's phone
  onValidationChange, // Callback to notify parent of validation state
  onCountryCodeChange, // Callback to notify parent when country code changes
  disabled = false,
  initialCountryCode = '+27', // Initial country code from parent
  ...formItemProps
}) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCode);
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);
  const [phoneError, setPhoneError] = useState(null);

  // Update country code when initialCountryCode prop changes
  useEffect(() => {
    if (initialCountryCode) {
      setSelectedCountryCode(initialCountryCode);
    }
  }, [initialCountryCode]);

  // Clear phone error when component unmounts or resets
  useEffect(() => {
    return () => {
      if (onValidationChange) {
        onValidationChange(null);
      }
    };
  }, []);

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(phoneError);
    }
  }, [phoneError, onValidationChange]);

  const checkPhoneNumber = async (phoneNumber) => {
    // Clear any previous API error first
    setPhoneError(null);
    
    // Only check via API if phone passes basic validation
    if (!phoneNumber || phoneNumber.length < 9) {
      return;
    }

    // Check if there are any Ant Design validation errors first
    const fieldErrors = form.getFieldError(name);
    if (fieldErrors && fieldErrors.length > 0) {
      // Don't run API check if there are form validation errors
      return;
    }

    try {
      setPhoneCheckLoading(true);
      const fullPhoneNumber = `${selectedCountryCode}${phoneNumber}`;
      
      // Skip check if it's the same user's current phone number
      if (currentPhoneNumber === fullPhoneNumber) {
        return;
      }
      
      const result = await businessUnitService.checkPhoneNumber(fullPhoneNumber);
      
      if (result.success && result.data.exists) {
        const error = {
          type: "error",
          message: result?.data.message || "Phone number already used. Please contact your administrator.",
        };
        setPhoneError(error);
      }
    } catch (error) {
      console.error('Phone check error:', error);
    } finally {
      setPhoneCheckLoading(false);
    }
  };

  const handlePhoneChange = (value) => {
    // Clean the phone number input - only allow digits
    let cleanPhone = value.replace(/[^\d]/g, '');
    
    // Don't allow more than 15 digits (E.164 international standard)
    if (cleanPhone.length > 15) {
      cleanPhone = cleanPhone.substring(0, 15);
    }
    
    // If it starts with 0, remove the leading 0 (normalize for international format)
    if (cleanPhone.startsWith('0')) {
      cleanPhone = cleanPhone.substring(1);
    }
    
    form.setFieldsValue({ [name]: cleanPhone });
    
    // Clear previous error when user starts typing
    setPhoneError(null);
    
    // Debounce the phone check
    clearTimeout(window.phoneCheckTimeout);
    window.phoneCheckTimeout = setTimeout(() => {
      checkPhoneNumber(cleanPhone);
    }, 500);
  };

  const handleCountryCodeChange = (value) => {
    setSelectedCountryCode(value);
    // Notify parent of country code change
    if (onCountryCodeChange) {
      onCountryCodeChange(value);
    }
    const currentPhone = form.getFieldValue(name);
    if (currentPhone) {
      // Re-check with new country code
      setTimeout(() => checkPhoneNumber(currentPhone), 100);
    }
  };

  // Get phone number value for external access
  const getPhoneNumberData = () => {
    const phoneNumber = form.getFieldValue(name);
    return phoneNumber ? {
      countryCode: selectedCountryCode,
      number: phoneNumber,
      full: `${selectedCountryCode}${phoneNumber}`
    } : null;
  };

  // Expose methods to parent
  React.useImperativeHandle(formItemProps.ref, () => ({
    getPhoneNumberData,
    setCountryCode: setSelectedCountryCode,
    clearError: () => setPhoneError(null)
  }));

  const rules = [
    ...(required ? [{ required: true, message: 'Please enter phone number' }] : []),
    { min: 7, message: 'Phone number must be at least 7 digits' },
    { max: 15, message: 'Phone number cannot exceed 15 digits' },
    { pattern: /^[0-9]*$/, message: 'Phone number must contain only digits' }
  ];

  return (
    <Form.Item
      name={name}
      label={label}
      rules={rules}
      validateStatus={
        form.getFieldError(name).length > 0
          ? 'error'
          : phoneError
          ? 'error'
          : ''
      }
      help={
        form.getFieldError(name).length > 0
          ? form.getFieldError(name)[0]
          : phoneError
          ? phoneError.message
          : 'Enter phone number without the country code'
      }
      {...formItemProps}
    >
      <Input
        addonBefore={
          <Select
            value={selectedCountryCode}
            onChange={handleCountryCodeChange}
            style={{ width: 180, fontSize: 12 }}
            dropdownStyle={{ minWidth: 280 }}
            disabled={disabled}
          >
            {countryCodes.map(country => (
              <Option key={country.code} value={country.dial} style={{ fontSize: 12 }}>
                {country.name} ({country.dial})
              </Option>
            ))}
          </Select>
        }
        prefix={<PhoneOutlined />}
        placeholder={placeholder}
        onChange={(e) => handlePhoneChange(e.target.value)}
        suffix={phoneCheckLoading ? <Spin size="small" /> : null}
        maxLength={15}
        size={size}
        disabled={disabled}
      />
    </Form.Item>
  );
};

export default PhoneNumberInput;